import type { Recipe } from "@/pages/sy/models/Recipe";

export const ROW_TYPE = {
  /** Parent row: the current version of the recipe family */
  PARENT: "parent",
  /** Child row: non-current versions of the recipe family */
  CHILD: "child",
} as const;

export type RecipeTreeRowType = (typeof ROW_TYPE)[keyof typeof ROW_TYPE];

export interface RecipeTreeRow {
  /** Unique row id: "recipe-{RecipeId}" */
  id: string;
  /** ag-Grid tree data path: ["ParentDisplayName"] for parents, ["ParentDisplayName", "ChildDisplayName"] for children */
  path: string[];
  /** Discriminator for rendering logic */
  rowType: RecipeTreeRowType;
  /** Display name: "RecipeName V{Version}" for IsCurrent, "V{Version}" for children */
  displayName: string;

  // Recipe fields (all rows have real recipe data)
  RecipeId: number | null;
  RecipeName: string;
  OriginalRecipeId: number | null;
  IsCurrent: boolean;
  IsUsed: boolean;
  Version: number;
  Author: string | null;
  CreationTimestamp: string | null;
  UpdateTimestamp: string | null;
  IsEnable: boolean;
  IsActive: boolean;
  Comments: string | null;
}

/** Type guard for parent rows (IsCurrent = true) */
export const isParentRow = (
  row: RecipeTreeRow,
): row is RecipeTreeRow & { rowType: "parent" } =>
  row.rowType === ROW_TYPE.PARENT;

/** Type guard for child rows (IsCurrent = false) */
export const isChildRow = (
  row: RecipeTreeRow,
): row is RecipeTreeRow & { rowType: "child" } =>
  row.rowType === ROW_TYPE.CHILD;

/** Extract a Recipe object from a tree row */
export const toRecipe = (row: RecipeTreeRow): Recipe => {
  const { id, path, rowType, displayName, ...recipe } = row;
  return recipe;
};
